import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { SalaryResultsType } from "../salario-liquido";

// Função utilitária para formatação de moeda brasileira
const formatarMoedaPtBR = (value: number): string => {
  return value.toFixed(2).replace('.', ',');
};

export const SalaryResponse = ({
  results,
}: {
  results: SalaryResultsType;
}) => {
  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado do Cálculo</MainCardTitle>
      
      {/* Cards de Resumo Principal */}
      <div className="grid sm:grid-cols-3 gap-4 mb-2 grid-cols-1">
        <div className="p-3 bg-primary rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center text-white font-semibold m-0">
            Salário Líquido
          </h2>
          <p className="sm:text-xl text-center font-bold text-md text-white m-0">
            <span className="text-sm mr-1 text-white">R$</span>
            {normalSanatizerBRL(results.salarioLiquido)}
          </p>
        </div>
        
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center font-semibold m-0">
            Salário Bruto
          </h2>
          <p className="sm:text-lg text-center font-black text-md text-red-800 m-0">
            <span className="text-sm mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(results.salarioBruto)}
          </p>
        </div>
        
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center font-semibold m-0">
            % Líquido
          </h2>
          <p className="sm:text-lg text-center font-black text-md text-black m-0">
            {formatarMoedaPtBR(results.percentualLiquido)}%
          </p>
        </div>
      </div>

      {/* Informação das Tabelas 2025 */}
      <div className="mb-6 p-4 bg-gray-50 rounded border text-center">
        <p className="text-sm text-gray-600">
          Cálculo com Tabelas 2025 Oficiais
        </p>
      </div>

      {/* Descontos Obrigatórios */}
      <h2 className="font-bold text-primary text-lg mt-8 text-center">
        Descontos Obrigatórios:
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-6 grid-cols-1">
        <div className="p-3 bg-red-50 rounded border border-red-200">
          <h3 className="text-sm font-semibold text-center mb-1">INSS</h3>
          <p className="text-lg font-bold text-center text-red-600">
            R$ {normalSanatizerBRL(results.inss.valor)}
          </p>
          <p className="text-xs text-center text-red-700">
            {formatarMoedaPtBR(results.inss.aliquotaEfetiva)}% efetiva
          </p>
        </div>
        
        <div className="p-3 bg-red-50 rounded border border-red-200">
          <h3 className="text-sm font-semibold text-center mb-1">IRRF</h3>
          <p className="text-lg font-bold text-center text-red-600">
            R$ {normalSanatizerBRL(results.irrf.valor)}
          </p>
          <p className="text-xs text-center text-red-700">
            {results.irrf.deducaoDependentes > 0 ? `Dedução: R$ ${normalSanatizerBRL(results.irrf.deducaoDependentes)}` : 'Sem deduções'}
          </p>
        </div>
      </div>

      {/* Outros Descontos - só mostra se houver */}
      {(results.pensaoAlimenticia > 0 || results.outrosDescontos > 0) && (
        <>
          <h2 className="font-bold text-primary text-lg my-6 text-center">
            Outros Descontos:
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-6 grid-cols-1">
            {results.pensaoAlimenticia > 0 && (
              <div className="p-3 bg-red-50 rounded border border-red-200">
                <h3 className="text-sm font-semibold text-center mb-1">Pensão Alimentícia</h3>
                <p className="text-lg font-bold text-center text-red-600">
                  R$ {normalSanatizerBRL(results.pensaoAlimenticia)}
                </p>
              </div>
            )}
            
            {results.outrosDescontos > 0 && (
              <div className="p-3 bg-red-50 rounded border border-red-200">
                <h3 className="text-sm font-semibold text-center mb-1">Outros Descontos</h3>
                <p className="text-lg font-bold text-center text-red-600">
                  R$ {normalSanatizerBRL(results.outrosDescontos)}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Benefícios - só mostra se houver salário-família */}
      {results.salarioFamilia.temDireito && (
        <>
          <h2 className="font-bold text-primary text-lg my-6 text-center">
            Benefícios:
          </h2>
          <div className="p-3 bg-green-50 rounded border border-green-200 mb-6">
            <h3 className="text-sm font-semibold text-center mb-1">Salário-Família</h3>
            <p className="text-lg font-bold text-center text-green-600">
              +R$ {normalSanatizerBRL(results.salarioFamilia.valor)}
            </p>
            <p className="text-xs text-center text-green-700">
              {results.salarioFamilia.quantidadeDependentes} dependente(s) × R$ 65,00
            </p>
          </div>
        </>
      )}

      {/* Informação sobre salário-família quando não tem direito */}
      {results.salarioFamilia.observacao && !results.salarioFamilia.temDireito && (
        <div className="mb-6 p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-sm text-blue-800 text-center">
            <strong>Informação:</strong> {results.salarioFamilia.observacao}
          </p>
        </div>
      )}

      {/* Total de Descontos */}
      <div className="p-4 bg-gray-50 rounded border-2 border-gray-300 mb-6">
        <div className="flex justify-between items-center">
          <span className="font-bold text-gray-700 text-lg">Total de Descontos:</span>
          <span className="font-bold text-xl text-red-600">
            R$ {normalSanatizerBRL(results.totalDescontos)}
          </span>
        </div>
      </div>

      {/* Detalhes dos Cálculos */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Detalhes dos Cálculos:
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
        <div className="p-3 bg-white rounded border">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">INSS:</h4>
          <p className="text-xs text-gray-600 mb-1">Base: R$ {normalSanatizerBRL(results.inss.baseCalculo)}</p>
          <p className="text-xs text-gray-600 mb-1">Alíquota Efetiva: {formatarMoedaPtBR(results.inss.aliquotaEfetiva)}%</p>
          <p className="text-xs text-gray-600">Teto: R$ 8.157,41</p>
        </div>
        
        <div className="p-3 bg-white rounded border">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">IRRF:</h4>
          <p className="text-xs text-gray-600 mb-1">Base: R$ {normalSanatizerBRL(results.irrf.baseCalculo)}</p>
          {results.irrf.deducaoDependentes > 0 && (
            <p className="text-xs text-gray-600 mb-1">Dedução Dependentes: R$ {normalSanatizerBRL(results.irrf.deducaoDependentes)}</p>
          )}
          <p className="text-xs text-gray-600">R$ 189,59 por dependente</p>
        </div>
      </div>

      {/* Informações sobre o Cálculo */}
      <div className="mt-6 p-4 bg-yellow-50 rounded border border-yellow-200">
        <h4 className="font-bold text-yellow-800 text-sm mb-2">
          Informações sobre o Cálculo - Atualização 2025
        </h4>
        <div className="text-sm text-yellow-800 space-y-1">
          <p>• Cálculos baseados nas tabelas oficiais de INSS e IRRF de 2025</p>
          <p>• INSS: Teto de contribuição R$ 8.157,41</p>
          <p>• IRRF: Dedução de R$ 189,59 por dependente</p>
          <p>• Salário-família: R$ 65,00 por dependente menor de 14 anos (renda até R$ 1.906,04)</p>
        </div>
        <div className="mt-2 p-2 bg-yellow-100 rounded border border-yellow-300">
          <p className="text-xs text-yellow-800">
            <strong>Fonte:</strong> Portaria MPS/MF nº 6/2025 e Lei nº 14.848/2024
          </p>
        </div>
      </div>
    </MainCard>
  );
};